// Phase 13 — task-activation cues (audio files).
//
// A short clip plays when a task enters its active window (README §6.7 timed
// windows), then the spoken announcement follows. The cue is the cook's
// choice from the cooking settings drawer (`cueSound` in the store), NOT a
// task-kind mapping:
//
//   whistle (referee) · chime · bell · drum · ping · beep · marimba
//
// The clips live in `public/sounds/` (served at `/sounds/*.wav`) and are
// authored by `scripts/generate-sounds.mjs`. They are played through
// `HTMLAudioElement`; the speaker chip mutes them entirely (preview ignores
// that mute — it is an explicit audition).
//
// Autoplay policy: browsers keep media muted until the page has had a user
// gesture. `ensureAudioReady` (wired to the speaker toggle / preview) and
// `installAudioUnlock` (a one-time gesture listener) prime the elements
// inside a real gesture so the first real cue isn't swallowed.
//
// All entry points are no-ops when the Audio constructor is unavailable
// (SSR, the test runner) so callers never have to guard.

import { laneCourse } from '@cookchow/shared';

const BASE = (import.meta.env?.BASE_URL ?? '/').replace(/\/?$/, '/');

export type CueSound = 'whistle' | 'chime' | 'bell' | 'drum' | 'ping' | 'beep' | 'marimba';

// The selectable cues, in drawer order. Whistle (referee) is the default.
export const CUE_SOUNDS: ReadonlyArray<{ id: CueSound; label: string }> = [
  { id: 'whistle', label: 'Whistle' },
  { id: 'chime', label: 'Chime' },
  { id: 'bell', label: 'Bell' },
  { id: 'drum', label: 'Drum' },
  { id: 'ping', label: 'Ping' },
  { id: 'beep', label: 'Beep' },
  { id: 'marimba', label: 'Marimba' },
];

const SRC: Record<CueSound, string> = {
  whistle: `${BASE}sounds/whistle.wav`,
  chime: `${BASE}sounds/chime.wav`,
  bell: `${BASE}sounds/bell.wav`,
  drum: `${BASE}sounds/drum.wav`,
  ping: `${BASE}sounds/ping.wav`,
  beep: `${BASE}sounds/beep.wav`,
  marimba: `${BASE}sounds/marimba.wav`,
};

const VOLUME = 0.6;

function audioAvailable(): boolean {
  return typeof Audio !== 'undefined';
}

// One preloaded element per clip, created lazily and reused.
const elements = new Map<string, HTMLAudioElement>();

function elementFor(src: string): HTMLAudioElement | null {
  if (!audioAvailable()) return null;
  let el = elements.get(src);
  if (!el) {
    el = new Audio(src);
    el.preload = 'auto';
    el.volume = VOLUME;
    elements.set(src, el);
  }
  return el;
}

function playSrc(src: string): void {
  const el = elementFor(src);
  if (!el) return;
  try {
    el.currentTime = 0;
    const p = el.play();
    // play() rejects under autoplay policy until unlocked — swallow it so a
    // muted session never throws into the render loop.
    if (p && typeof p.catch === 'function') p.catch(() => undefined);
  } catch {
    // Older engines throw synchronously; stay calm.
  }
}

// Prime every clip inside a user gesture: a muted play→pause→reset run that
// satisfies the autoplay policy so later real plays are allowed. Also primes
// the speech engine (see below) in the same gesture — with sound on by
// default the cook never presses the speaker toggle, so this gesture-unlock
// path is what lets the very first spoken announcement sound. Idempotent.
let unlocked = false;
export function ensureAudioReady(): void {
  if (unlocked) return;
  unlocked = true;
  primeSpeech();
  if (!audioAvailable()) return;
  for (const src of Object.values(SRC)) {
    const el = elementFor(src);
    if (!el) continue;
    el.muted = true;
    try {
      const p = el.play();
      const finish = (): void => {
        el.pause();
        el.currentTime = 0;
        el.muted = false;
      };
      if (p && typeof p.then === 'function') {
        p.then(finish).catch(() => {
          el.muted = false;
        });
      } else {
        finish();
      }
    } catch {
      el.muted = false;
    }
  }
}

// Register one-time listeners that unlock audio on the first user gesture
// anywhere on the page. Returns a cleanup that removes them.
export function installAudioUnlock(): () => void {
  if (typeof document === 'undefined') return () => undefined;
  const events: Array<keyof DocumentEventMap> = ['pointerdown', 'keydown', 'touchstart'];
  const onGesture = (): void => {
    ensureAudioReady();
    remove();
  };
  const remove = (): void => {
    for (const e of events) document.removeEventListener(e, onGesture);
  };
  for (const e of events) document.addEventListener(e, onGesture, { once: true, passive: true });
  return remove;
}

// Play one of the selectable cues.
export function playCue(cue: CueSound): void {
  playSrc(SRC[cue]);
}

// Audition a cue from the settings drawer. Unlocks audio (the click is a
// gesture) and plays the clip directly — deliberately IGNORING the speaker
// mute, since the cook is actively choosing a sound. Never chains speech.
export function previewCue(cue: CueSound): void {
  ensureAudioReady();
  playSrc(SRC[cue]);
}

// ---- Spoken task announcements -------------------------------------------
//
// On a timed task's activation the cook hears the single whistle (above) and
// then a spoken line: the task's course word ("Alert" / "Starter" / "Main" /
// "Dessert", via the shared `laneCourse`) followed by the task's action text.
// Speech rides the browser-local Web Speech API — no network, no CSP change,
// keeping the closed-world posture (README §3.2). Every entry point no-ops
// when the API is absent (SSR / the test runner) so callers never guard.

// Every cue clip is well under this; we chain speech onto the cue's `ended`
// event, with this as a fallback for engines (and the test stub) that never
// dispatch `ended`. Sized to the longest cue. Whichever fires first speaks once.
const CUE_FALLBACK_MS = 1200;

function speechAvailable(): boolean {
  return (
    typeof window !== 'undefined' &&
    'speechSynthesis' in window &&
    typeof SpeechSynthesisUtterance !== 'undefined'
  );
}

// Resolve an English voice once. `getVoices()` is empty until the engine has
// loaded its list, so we stay unresolved (returning undefined → the default
// voice) until it populates, then cache.
let cachedVoice: SpeechSynthesisVoice | undefined;
let voiceResolved = false;
function pickVoice(): SpeechSynthesisVoice | undefined {
  if (voiceResolved) return cachedVoice;
  if (!speechAvailable()) return undefined;
  const voices = window.speechSynthesis.getVoices?.() ?? [];
  if (voices.length === 0) return undefined;
  voiceResolved = true;
  cachedVoice = voices.find((v) => /^en(-|$)/i.test(v.lang)) ?? voices[0];
  return cachedVoice;
}

export interface SpeakOptions {
  /** `utterance.rate` (speech speed multiplier). Default 1. */
  rate?: number;
  /** `utterance.pitch` (0–2). Default 1. */
  pitch?: number;
  /** Explicit voice; falls back to an auto-picked English voice. */
  voice?: SpeechSynthesisVoice;
}

// Speak a line at the given rate/pitch/voice (calm defaults). The rate comes
// from the speech-speed dropdown and the voice/pitch from the chosen persona
// (see voicePersonas.ts); the two are orthogonal. Idempotent-safe to call.
export function speak(text: string, opts: SpeakOptions = {}): void {
  if (!speechAvailable() || !text) return;
  try {
    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = opts.rate ?? 1;
    utter.pitch = opts.pitch ?? 1;
    utter.volume = VOLUME;
    const voice = opts.voice ?? pickVoice();
    if (voice) utter.voice = voice;
    window.speechSynthesis.speak(utter);
  } catch {
    // Calm: a synthesiser that won't speak is never worth surfacing.
  }
}

// Cancel any in-flight or queued speech (used when the speaker is muted
// mid-sentence — the spoken line can run for seconds, unlike the cues).
export function cancelSpeech(): void {
  if (!speechAvailable()) return;
  try {
    window.speechSynthesis.cancel();
  } catch {
    // Calm.
  }
}

// Audition the spoken voice from the settings drawer at the chosen rate /
// pitch / voice. Unlocks audio (the ▶ click is a gesture), cancels any
// in-flight preview so repeated taps don't stack, and speaks — deliberately
// IGNORING the speaker mute (an explicit audition). Cue-free.
export function previewSpeech(text: string, opts: SpeakOptions = {}): void {
  ensureAudioReady();
  cancelSpeech();
  speak(text, opts);
}

// The cue element currently sounding an announcement, so a mute can stop it.
let activeCueEl: HTMLAudioElement | null = null;

// Stop the in-flight cue clip and rewind it, so muting silences it immediately.
export function stopCue(): void {
  if (!activeCueEl) return;
  try {
    activeCueEl.pause();
    activeCueEl.currentTime = 0;
  } catch {
    // Calm.
  }
}

// Warm the speech engine inside a user gesture (called from ensureAudioReady).
// `resume()` clears the suspended state some engines start in; touching
// `getVoices` nudges the async voice list to load.
function primeSpeech(): void {
  if (!speechAvailable()) return;
  try {
    window.speechSynthesis.resume();
    pickVoice();
  } catch {
    // Calm.
  }
}

// A single in-flight announcement (we only ever voice one task at a time —
// the first newly-active per tick). Holds the teardown for its pending
// whistle→speech chain so a mute can cancel it before it speaks.
let pendingAnnounce: { clear: () => void } | null = null;

function clearPendingAnnounce(): void {
  if (pendingAnnounce) {
    pendingAnnounce.clear();
    pendingAnnounce = null;
  }
}

export interface ActivationAnnouncement extends SpeakOptions {
  lane: string;
  action: string;
  /** Which cue clip to sound before the spoken line. Default 'whistle'. */
  cue?: CueSound;
}

/**
 * Announce a task's activation: play the chosen cue, then speak
 * "<course>. <action>" once the cue ends (fallback timer for engines that
 * don't fire `ended`), at the given rate/pitch/voice. Replaces any
 * still-pending announcement, since only the first newly-active task per tick
 * is voiced.
 */
export function playActivation({
  lane,
  action,
  cue = 'whistle',
  rate,
  pitch,
  voice,
}: ActivationAnnouncement): void {
  clearPendingAnnounce();
  const course = laneCourse(lane);
  const phrase = `${course ? `${course}. ` : ''}${action ?? ''}`.replace(/\s+/g, ' ').trim();

  let spoken = false;
  const sayOnce = (): void => {
    if (spoken) return;
    spoken = true;
    clearPendingAnnounce();
    speak(phrase, { rate, pitch, voice });
  };

  const el = elementFor(SRC[cue]);
  if (!el) {
    // No audio element (SSR / no Audio) — still voice the line.
    sayOnce();
    return;
  }

  activeCueEl = el;
  const onEnded = (): void => sayOnce();
  el.addEventListener('ended', onEnded, { once: true });
  const timer = typeof setTimeout !== 'undefined' ? setTimeout(sayOnce, CUE_FALLBACK_MS) : null;
  pendingAnnounce = {
    clear: () => {
      spoken = true;
      if (timer) clearTimeout(timer);
      el.removeEventListener('ended', onEnded);
    },
  };
  playSrc(SRC[cue]);
}

// Silence everything immediately: drop any pending announcement, stop the
// in-flight cue, and cancel speech. Called when the speaker toggle is muted.
export function stopAnnouncements(): void {
  clearPendingAnnounce();
  stopCue();
  cancelSpeech();
}
